import { useState, type FormEvent } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/categories";

const navLinks = [
  { to: "/", label: "首页" },
  { to: "/about", label: "关于我们" },
  { to: "/faq", label: "常见问题" },
  { to: "/favorites", label: "我的收藏" },
];

export default function Navbar() {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    const value = keyword.trim();
    if (!value) return;
    navigate(`/search?q=${encodeURIComponent(value)}`);
    setMobileOpen(false);
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `relative whitespace-nowrap px-3 py-2 text-sm font-medium transition-colors ${
      isActive ? "text-accent-400" : "text-background-100 hover:text-accent-300"
    }`;

  return (
    <header className="sticky top-0 z-50 w-full bg-primary-800 text-background-50">
      <div className="mx-auto flex h-16 w-full items-center gap-4 px-4 md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2" onClick={() => setMobileOpen(false)}>
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-500 text-primary-950">
            <i className="ri-play-fill text-xl" />
          </span>
          <span className="font-heading text-xl font-bold tracking-tight text-background-50">
            91<span className="text-accent-400">免费</span>
          </span>
        </Link>

        <nav className="hidden items-center md:flex">
          {navLinks.slice(0, 1).map((link) => (
            <NavLink key={link.to} to={link.to} className={linkClass} end>
              {link.label}
            </NavLink>
          ))}

          <div
            className="relative"
            onMouseEnter={() => setCategoryOpen(true)}
            onMouseLeave={() => setCategoryOpen(false)}
          >
            <button
              type="button"
              className="flex items-center gap-1 whitespace-nowrap px-3 py-2 text-sm font-medium text-background-100 transition-colors hover:text-accent-300"
            >
              影视分类
              <i className={`ri-arrow-down-s-line transition-transform ${categoryOpen ? "rotate-180" : ""}`} />
            </button>
            {categoryOpen && (
              <div className="absolute left-0 top-full w-52 rounded-lg bg-background-50 p-2 ring-1 ring-background-200">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-800 transition-colors hover:bg-primary-50 hover:text-primary-700"
                  >
                    <i className={`${cat.icon} text-base text-primary-500`} />
                    {cat.name}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {navLinks.slice(1).map((link) => (
            <NavLink key={link.to} to={link.to} className={linkClass}>
              {link.label}
            </NavLink>
          ))}
        </nav>

        <form onSubmit={handleSearch} className="ml-auto hidden max-w-xs flex-1 md:block">
          <div className="relative">
            <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400" />
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影视作品..."
              className="w-full rounded-md border border-primary-600 bg-primary-900/60 py-2 pl-9 pr-3 text-sm text-background-50 outline-none transition-colors placeholder:text-background-300 focus:border-accent-400 focus:bg-primary-900"
            />
          </div>
        </form>

        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          className="ml-auto flex h-10 w-10 items-center justify-center rounded-md text-2xl text-background-50 md:hidden"
          aria-label="打开菜单"
        >
          <i className={mobileOpen ? "ri-close-line" : "ri-menu-line"} />
        </button>
      </div>

      {mobileOpen && (
        <div className="border-t border-primary-700 bg-primary-800 px-4 pb-4 pt-3 md:hidden">
          <form onSubmit={handleSearch} className="mb-3">
            <div className="relative">
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400" />
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影视作品..."
                className="w-full rounded-md border border-primary-600 bg-primary-900/60 py-2.5 pl-9 pr-3 text-sm text-background-50 outline-none placeholder:text-background-300 focus:border-accent-400"
              />
            </div>
          </form>
          <div className="flex flex-col gap-1">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                    isActive ? "bg-primary-900 text-accent-400" : "text-background-100 hover:bg-primary-900"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <div className="mt-2 border-t border-primary-700 pt-3">
              <p className="mb-2 px-3 text-xs font-semibold uppercase tracking-wide text-background-400">
                影视分类
              </p>
              <div className="grid grid-cols-2 gap-1">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-background-100 transition-colors hover:bg-primary-900"
                  >
                    <i className={`${cat.icon} text-base text-accent-400`} />
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}